import React from 'react';

import { Flex, Box } from 'reflexbox';
import {
  Heading,
  Text,
} from 'rebass';
import { format } from 'date-fns';

import PageHelmet from 'frontend/components/PageHelmet';

import SharedConfig from 'shared/config';

import { Link } from 'react-router';

/* eslint-disable react/prop-types */
const Para = ({ children }) => (
  <Text my={2}>
    {children}
  </Text>
);

const Item = ({ children }) => (
  <li>
    <Text my={1}>
      {children}
    </Text>
  </li>
);

const PrivacyPolicyLink = ({ children }) => (<Link to="/privacy">{children}</Link>);
const LinkNewTab = ({ children, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);
/* eslint-enable react/prop-types */

export const TermsOfUse = () => (
  <>
    <Heading level={2} mb={1}>Raidbots Terms of Use Agreement</Heading>

    <Para>
      <strong>Last Updated: {format(SharedConfig.legal.touVersion, 'yyy-MM-dd')}</strong>
    </Para>

    <Para>
      This Terms of Use Agreement (the “Agreement”) constitutes a legal agreement between you and Raidbots LLC
      (“Raidbots”) and governs your access to and use of Raidbots’ services, including the Raidbots.com website (the
      “Site”) and Raidbots software (collectively, “Services”). Some of the Services are made available without cost
      (“Free Services”), while the use of other Services requires a paid subscription (“Paid Services”). Your access to
      and use of Paid Services is subject to our Purchase and Payment Terms set forth after the end of this Agreement.
    </Para>

    <Para>
      BY CHECKING A BOX OR CLICKING A BUTTON INDICATING YOUR ASSENT TO THIS AGREEMENT OR BY ACCESSING OR USING THE
      SERVICES, YOU AGREE TO ALL OF THE TERMS AND CONDITIONS OF THIS AGREEMENT AND THE RAIDBOTS
      {' '}<PrivacyPolicyLink>PRIVACY POLICY</PrivacyPolicyLink>. IF YOU DO NOT AGREE TO ALL OF THE TERMS AND CONDITIONS
      OF THIS AGREEMENT AND THE PRIVACY POLICY, YOU MAY NOT USE OR ACCESS THE SERVICES.
    </Para>

    <ol>
      <Item>
        <strong>Eligibility.</strong>
        {' '}
        You may not use the Services if you are under the age of 13. If you are 13 or older, you may use the Services
        only if you are over the minimum age of consent required for entering into a valid contract in your country or
        jurisdiction. If you are 13 to 18, you may only use the Services with the permission of your parent or legal
        guardian. If you’re a parent or legal guardian, and you allow your child to use the Services, then these terms
        apply to you and you’re responsible for your child’s activity on the Services. If Raidbots previously disabled
        your account for violating this Agreement or any of our policies, or for legal reasons, you may not create a new
        Raidbots account or use the Services without our written permission.
      </Item>

      <Item>
        <strong>License Grant.</strong>
        {' '}
        Subject to your compliance with this Agreement, we grant you a limited, non-exclusive,
        non-transferable, non-sublicensable, and revocable license to access and use the Services solely as contemplated
        under this Agreement. All rights not expressly granted to you in this Agreement are reserved by Raidbots and our
        licensors.
      </Item>

      <Item>
        <strong>Restrictions.</strong>
        {' '}
        In connection with your access to or use of the Services, you are prohibited from:
        <ol style={{ listStyleType: 'lower-alpha' }}>
          <Item>
            accessing data not intended for you or logging onto a server or an account which you are not authorized to
            access;
          </Item>
          <Item>
            using any engine, software, tool, agent or other device or mechanism (including without limitation browsers,
            spiders, robots, avatars or intelligent agents) to access, navigate, or search the Site, other than
            generally available third-party web browsers (e.g., Mozilla Firefox, Google Chrome, Microsoft Edge);
          </Item>
          <Item>
            using any device, software or routine to interfere or attempt to interfere with the proper working of the
            Services or any activity being conducted on the Site, or taking any action that imposes an unreasonably or
            disproportionately large load on the Raidbots network or infrastructure;
          </Item>
          <Item>
            attempting to probe, scan, or test the vulnerability of a Raidbots system or network or to breach security
            or authentication measures without proper authorization;
          </Item>
          <Item>
            attempting to interfere with service to any user, host or network, including, without limitation, via means
            of submitting a virus to the Site or through the Service, overloading, “flooding,” “spamming,” “mailbombing”
            or “crashing”;
          </Item>
          <Item>
            copying, modifying, adapting, reproducing, reverse engineering, decompiling, disassembling, or preparing
            derivative works from Raidbots software, Raidbots Content or User Content that is not your own;
          </Item>
          <Item>
            using or attempting to use any Raidbots Content, User Content, or any other data or information found on or
            accessed through the Services as input into or for the development or training of generative artificial
            intelligence, machine learning, algorithms, software, or related technologies;
          </Item>
          <Item>
            distributing viruses, malicious code, malware or any other technologies that may harm Raidbots, the
            Services, or a user of the Services;
          </Item>
          <Item>
            violating any applicable laws, rules, or regulations;
          </Item>
          <Item>
            circumventing or violating any Raidbots technical measures, security measures, or policies;
          </Item>
          <Item>
            violating, infringing, or breaching the rights of Raidbots or a third-party, including, but not limited to
            any copyrights, trademark rights, patent rights, trade secrets rights, rights of publicity or personality,
            moral rights, or any other proprietary rights; or
          </Item>
          <Item>
            submitting to or through the Services any false, inaccurate, misleading, deceptive, defamatory, or libelous
            materials or User Content.
          </Item>
        </ol>
      </Item>

      <Item>
        <strong>Accounts</strong>
        <ol>
          <Item>
            You have the option to create a Raidbots account. If you wish to purchase Paid Services, you must create a
            Raidbots account. During the account creation process, we will ask you for information, which may include
            personal information (“Account Information”). You must provide accurate and complete Account Information,
            and you must keep that Account Information current.
          </Item>
          <Item>
            You are fully responsible for all activity that occurs under your Raidbots account, including for any
            actions taken by persons to whom you have granted access to the account. You are responsible for taking all
            reasonable steps to ensure that no unauthorized person has access to or uses your password or your account.
            Therefore, please keep your password confidential. If you believe that the security of your account has been
            compromised at any time (e.g., through theft or the unauthorized use of your password), please notify us
            promptly at support@raidbots.com.
          </Item>
          <Item>
            Raidbots may suspend or terminate your Raidbots account at any time, for any reason. If you have purchased
            Paid Services and Raidbots suspends or terminates your account for any reason other than your breach of this
            Agreement, Raidbots will provide a pro-rata refund of the Fees paid by you for the Paid Services applicable
            to the period following the date of suspension or termination.
          </Item>
        </ol>
      </Item>

      <Item>
        <strong>Site Content</strong>
        <ol>
          <Item>
            <ol>
              <strong>User Content</strong>
              <ol>
                <Item>
                  The Services may include functionality for uploading or providing feedback, suggestions, information,
                  and materials (“User Content”). You grant Raidbots, its successors and assigns, a worldwide,
                  non-exclusive, royalty-free, fully-paid, transferable, irrevocable, perpetual, and sub-licensable
                  right to use, reproduce, modify, adapt, publish, prepare derivative works of, distribute, publicly
                  perform, and publicly display your User Content throughout the world in any media for the purpose of
                  providing the Services; provided that we will only use your Account Information for purposes of
                  creating, maintaining, and administering your account and providing the Services to you. You retain
                  ownership of and all rights in your User Content, subject only to the limited license you grant to us
                  in this Agreement.
                </Item>
                <Item>
                  You will not upload or provide User Content or otherwise post, transmit, distribute, or disseminate
                  through the Services any material that: (a) is false, misleading, unlawful, obscene, indecent, lewd,
                  pornographic, defamatory, libelous, threatening, harassing, hateful, abusive, or inflammatory; (b)
                  encourages conduct that would be considered a criminal offense or gives rise to civil liability; (c)
                  breaches or infringes any duty toward or rights of any person or entity, including rights of
                  publicity, privacy or intellectual property rights; (d) contains corrupted data or any other harmful,
                  disruptive, or destructive files; or (e) in our sole judgment, is objectionable, restricts or inhibits
                  any person or entity from using or enjoying any portion of the Services, or which may expose Raidbots
                  or its customers or other persons to harm or liability of any nature.
                </Item>
                <Item>
                  We have no obligation to monitor any User Content. However, we reserve the right to prohibit the
                  uploading or submission of User Content and to remove User Content at any time and for any reason
                  without notice. We take no responsibility and assume no liability for any User Content, including any
                  loss or damage to any of your User Content. You should back up any User Content that you upload or
                  submit to the Services.
                </Item>
                <Item>
                  You may submit comments, suggestions, and ideas about the Services (“Feedback”). Submitting Feedback
                  is entirely voluntary, and Raidbots will be free to use any Feedback you submit as we fit without any
                  obligation to you.
                </Item>
                <Item>
                  You are solely responsible for backing up all User Content you submit to or through the Services. To
                  the fullest extent permitted by applicable law, Raidbots may delete any User Content you submit, at
                  any time, with or without notice to you, and without incurring any liability to you.
                </Item>
              </ol>
            </ol>
          </Item>
          <Item>
            <strong>Raidbots Content and Intellectual Property Rights.</strong>
            {' '}
            All rights, title, and interest in and to the Services and its components (including all intellectual
            property rights) will remain with and belong exclusively to Raidbots or its licensors, including the rights
            to any text, graphics, images, music, software, audio, video, documents, works of authorship of any kind,
            and information or other materials that are made available by us through the Services, excluding User
            Content (“Raidbots Content”). The Services, Raidbots Content, the Raidbots name, and the Raidbots logo are
            protected by copyright, trademark, patent, and/or other laws of the United States and other countries. This
            Agreement does not grant you any rights to Raidbots trademarks or service marks.
          </Item>
        </ol>
      </Item>
      <Item>
        <strong>Third-Party Sites and Services.</strong>
        {' '}
        References on the Site to any third parties, including persons or companies, and to any products or services of
        such third parties, are provided solely as a convenience to you. Raidbots does not endorse, recommend, approve
        of, or make any representations or warranties regarding any third parties or their products or services.
        Raidbots may provide links to third-party websites. Raidbots is not responsible for the content of any
        third-party websites and does not make any representations regarding the content or accuracy of material on such
        websites.  Third-party websites and services are governed by the terms and policies of their third-party
        providers (“Third-Party Terms”), and not the terms and policies of Raidbots. You may be required to agree to
        Third-Party Terms if you visit a third-party website or use a third-party service. If you decide to follow a
        link to any third-party website, you do so entirely at your own risk.
      </Item>
      <Item>
        <strong>Privacy.</strong>
        {' '}
        The Raidbots <PrivacyPolicyLink>Privacy Policy</PrivacyPolicyLink> explains how Raidbots collects, stores, uses,
        shares, and protects the personal information you provide to Raidbots. Please familiarize yourself with the
        Privacy Policy prior to using the Services.
      </Item>
      <Item>
        <strong>Warranty Disclaimer.</strong>
        {' '}
        THE SERVICES ARE PROVIDED “AS IS” WITHOUT REPRESENTATION OR WARRANTY, WHETHER IT IS
        EXPRESS, IMPLIED, OR STATUTORY. WITHOUT LIMITING THE FOREGOING, TO THE FULLEST EXTENT PERMITTED BY APPLICABLE
        LAW, RAIDBOTS SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, OR NON-INFRINGEMENT. RAIDBOTS DOES NOT WARRANT, REPRESENT OR GUARANTEE IN ANY WAY THAT THE SERVICES ARE
        ACCURATE, RELIABLE, OR CORRECT; THAT THE SERVICES WILL MEET YOUR REQUIREMENTS; THAT THE SERVICES WILL BE
        AVAILABLE AT ANY PARTICULAR TIME OR LOCATION, UNINTERRUPTED, ERROR-FREE, WITHOUT DEFECT OR SECURE; THAT ANY
        DEFECTS OR ERRORS IN THE SERVICES WILL BE CORRECTED; OR THAT THE SERVICES ARE FREE OF VIRUSES OR HARMFUL CODE.
      </Item>
      <Item>
        <strong>Limitation of Liability</strong>
        <ol>
          <Item>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL RAIDBOTS BE LIABLE FOR ANY DIRECT,
            INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION
            DAMAGES FOR LOSS OF GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, THAT RESULT FROM THE USE OF, INABILITY
            TO USE, OR UNAVAILABILITY OF THE SERVICES. IN NO EVENT WILL RAIDBOTS WILL NOT BE LIABLE FOR ANY LOSS OR
            DAMAGE THAT IS NOT REASONABLY FORESEEABLE.
          </Item>
          <Item>
            IN NO EVENT WILL RAIDBOTS BE RESPONSIBLE FOR ANY DAMAGE, LOSS, OR INJURY RESULTING FROM HACKING, TAMPERING,
            OR OTHER UNAUTHORIZED ACCESS TO OR USE OF THE SERVICES OR YOUR RAIDBOTS ACCOUNT, OR THE INFORMATION
            CONTAINED THEREIN.
          </Item>
          <Item>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE TOTAL LIABILITY OF RAIDBOTS IS LIMITED TO THE GREATER
            OF (A) THE AMOUNT OF FEES PAID BY YOU TO RAIDBOTS FOR YOUR USE OF THE SERVICES DURING THE THREE (3) MONTH
            PERIOD IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM FOR LIABILITY, OR (B) $100.
          </Item>
          <Item>
            THE LIMITATION OF LIABILITY SET FORTH IN THIS SECTION APPLIES WHETHER THE ALLEGED LIABILITY IS BASED ON
            CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR ANY OTHER BASIS, AND EVEN IF RAIDBOTS HAS BEEN ADVISED OF
            THE POSSIBILITY OF SUCH DAMAGE.
          </Item>
        </ol>
      </Item>

      <Item>
        <strong>Indemnification.</strong>
        {' '}
        You will indemnify, defend, and hold Raidbots and our employees, members, directors, agents,
        and representatives harmless from and against any and all claims, costs, losses, damages, judgments,
        assessments, penalties, interest, and expenses (including without limitation reasonable attorneys’ fees) arising
        out of or in connection with any claim, action, audit, investigation, inquiry, or other proceeding instituted by
        any person or entity that arises out of or relates to: (a) any actual or alleged breach of your representations,
        warranties, or obligations set forth in this Agreement; (b) your wrongful or improper use of the Services; (c)
        your violation of any third-party right, including without limitation any right of privacy, publicity rights or
        intellectual property rights; or (d) your violation of any law, rule or regulation of the United States or any
        other country.
      </Item>

      <Item>
        <strong>Miscellaneous Terms.</strong>
        <ol>
          <Item>
            <strong>Changes to this Agreement.</strong>
            {' '}
            We may amend this Agreement at any time with notice that we deem to be reasonable
            under the circumstances, by posting the revised version on the Site, or communicating it to you through the
            Services (each a “Revised Version”). The Revised Version will be effective as of the time it is posted but
            will not apply retroactively. Your use of the Services after we post a Revised Version constitutes your
            acceptance of such Revised Version.
          </Item>
          <Item>
            <strong>Changes to the Services.</strong>
            {' '}
            We may make changes or updates to the Services available to you, which you must
            accept to continue using the Services. Any such changes or updates may be subject to additional terms made
            known to you at that time. We may suspend or cease provision of the Services, in whole or in part, at any
            time, with or without notice to you.
          </Item>
          <Item>
            <strong>Severability.</strong>
            {' '}
            If any provision of this Agreement is invalid or unenforceable under applicable law, then it
            will be changed and interpreted to accomplish the objectives of such provision to the greatest extent
            possible under applicable law, and the remaining provisions of this Agreement will continue in full force
            and effect.
          </Item>
          <Item>
            <strong>Waiver.</strong>
            {' '}
            No waiver of any term or condition of this Agreement will be deemed a further or continuing waiver
            of such term or conditions or any other term or condition.
          </Item>
          <Item>
            <strong>Assignment.</strong>
            {' '}
            Unless expressly authorized by Raidbots, this Agreement, and any rights and licenses granted
            hereunder, may not be transferred or assigned by you and any attempted transfer or assignment will be null
            and void.
          </Item>
          <Item>
            <strong>Force majeure.</strong>
            {' '}
            Neither party will be liable for any failure in performance due to causes beyond that party’s
            reasonable control (such as fire, explosion, power blackout, earthquake, flood, severe storms, strike,
            embargo, labor disputes, acts of civil or military authority, war, terrorism (including cyber terrorism),
            acts of God, acts or omissions of Internet carriers, acts or omissions of regulatory or governmental bodies
            (including the passage of laws or regulations or other acts of government that impact the delivery of the
            Services)). This section will not, however, apply to your payment obligations.
          </Item>
          <Item>
            <strong>Electronic Communications.</strong>
            {' '}
            You consent to receive communications from us, including email messages to the
            email address you provide to us if you sign-up for a Raidbots account or update your account. Such
            communications may include, but are not limited to requests for secondary authentication, receipts,
            reminders, notifications regarding updates to your account or account support, and marketing or promotional
            communications. You acknowledge that you are not required to consent to receive marketing and promotional
            communications as a condition of using the Services. You may opt-out of receiving marketing and promotional
            email communications we send to you by following the unsubscribe options on such emails.
          </Item>
          <Item>
            <strong>Governing Law; Jurisdiction and Venue.</strong>
            {' '}
            This Agreement will be construed in accordance with and governed
            exclusively by the laws of the State of Washington applicable to agreements made among Washington residents
            and to be performed wholly within Washington, regardless of the parties’ actual domiciles. Any legal action
            or proceeding arising under or in connection with this Agreement will be brought exclusively in courts
            located in King County, Washington, and Raidbots and you irrevocably consent to the personal jurisdiction
            and venue in such courts.
          </Item>
          <Item>
            <strong>Entire Agreement.</strong>
            {' '}
            This Agreement, and any applicable additional terms and policies incorporated into or
            referenced by this Agreement, constitutes the complete statement of the agreement between you and Raidbots
            regarding the Services. In the event of a conflict between this Agreement and any other Raidbots terms or
            policies, this Agreement will prevail and control the subject matter of such conflict.
          </Item>
        </ol>
      </Item>
    </ol>

    <hr />

    <Heading level={2} mb={1}>RAIDBOTS PURCHASE AND PAYMENT TERMS</Heading>

    <Para>
      If you purchase Paid Services, you agree to these Purchase and Payment Terms, which are in addition to the terms
      and conditions set forth in the Terms of Use Agreement.
    </Para>

    <ol>
      <Item>
        <strong>Subscriptions; Fees; Payment Methods.</strong>
        {' '}
        Raidbots Paid Services include subscriptions to premium services (a
        “Subscription”). Different Subscription levels and Subscription durations are available and are described in the
        support section of the Site and, if you have created a Raidbots account, within your account interface.
        Subscriptions require the payment of fees (“Fees”). Before you pay any Fees, we will provide you with an
        opportunity to review Fees. Raidbots may specify the payment method(s) by which you must make payments. If you
        pay by debit or credit card, your debit or credit card number are processed by Raidbots’ third-party payment
        processor and are not stored on Raidbots’ systems. By providing your authorized payment method and electing to
        purchase a Subscription, you authorize Raidbots or its third-party payment processor to charge your authorized
        payment method for all amounts due.
      </Item>
      <Item>
        <strong>Taxes.</strong>
        {' '}
        Unless stated otherwise, all Fees are exclusive of any and all applicable federal, state or local use,
        excise, value-added, gross receipts, sales taxes, duties, universal service assessments or other similar
        liabilities, other than general income or property taxes imposed on Raidbots. (“Taxes”). All such Taxes, however
        denominated, that are levied on the Paid Services provided to you or Fees paid by you, chargeable to or against
        Raidbots by any governmental authority, shall be passed through to, and shall be payable by, you. However,
        nothing in this section shall be construed to make you responsible for any general income or gross receipts tax
        due from and chargeable against Raidbots.
      </Item>
      <Item>
        <strong>Subscription Term; Automatic Renewal of Subscription.</strong>
        {' '}
        By purchasing a Subscription, you agree that your
        Subscription will continue for the period of time you select at the time of purchase (the “Subscription Term”).
        YOU FURTHER AGREE THAT AT THE END OF EACH SUBSCRIPTION TERM, YOUR SUBSCRIPTION WILL AUTOMATICALLY RENEW FOR A
        SUBSCRIPTION TERM OF EQUAL DURATION UNLESS AND UNTIL WE TERMINATE YOUR SUBSCRIPTION OR YOU CANCEL YOUR
        SUBSCRIPTION AS DESCRIBED BELOW IN SECTION 7 OF THESE PURCHASE AND PAYMENT TERMS. We will provide you with
        advance notice of the end of your Subscription Term as required under applicable law.
      </Item>
      <Item>
        <strong>Automatically Recurring Fees and Payment.</strong>
        {' '}
        UNLESS WE TERMINATE YOUR SUBSCRIPTION OR YOU CANCEL YOUR SUBSCRIPTION
        PRIOR TO THE RENEWAL OF YOUR SUBSCRIPTION TERM, THEN FOR EACH SUCH RENEWAL SUBSCRIPTION TERM, YOUR AUTHORIZED
        PAYMENT METHOD WILL AUTOMATICALLY BE CHARGED THE SUBSCRIPTION FEE IN EFFECT AT THE TIME OF SUCH RENEWAL, PLUS
        ANY TAXES, ON THE APPLICABLE ANNIVERSARY (WHETHER MONTHLY, EVERY 3 MONTHS OR EVERY 6 MONTHS) OF YOUR BILLING
        DATE; PROVIDED THAT IF SUCH SUBSCRIPTION FEES ARE HIGHER THAN THE FEES PAID BY YOU FOR THE IMMEDIATELY PRECEDING
        SUBSCRIPTION TERM, WE WILL PROVIDE YOU NOTICE OF THE NEW FEES PRIOR TO THE TIME OF THE RENEWAL. If the billing
        date of your Subscription is on the 29th, 30th, or 31st day of a month, and your billing date does not exist in
        a particular month because there are fewer than 29, 30, or 31 days, as applicable, then your billing date for
        that particular renewal will be on the last day of the month in which your billing date would otherwise fall.
      </Item>
      <Item>
        <strong>Interest and Late Charges.</strong>
        {' '}
        If you fail to pay amounts due when such amounts are due, late charges of the lesser
        of one and one-half percent (1.5%) per month or the maximum allowable under applicable law may also become
        payable by you to Raidbots. If you fail to pay any amount payable hereunder when due, you agree to pay, in
        addition to the past due amount and any applicable interest charges, all reasonable expenses incurred by
        Raidbots in collecting past due amounts, including, but not limited to, all expenses of any legal proceeding
        related to the past due amount and all reasonable attorneys’ fees incurred in connection with such proceeding.
        No failure by Raidbots to request payment of any past due amount shall be deemed a waiver by Raidbots of your
        payment obligations.
      </Item>
      <Item>
        <strong>Subscription Changes.</strong>
        {' '}
        You may change your Subscription level or Subscription Term through your account
        interface. For more information, please refer to
        {' '}
        <LinkNewTab
          href="https://support.raidbots.com/article/51-how-can-i-change-my-subscription-level"
        >
          this section
        </LinkNewTab> of our support pages.
      </Item>
      <Item>
        <strong>Subscription Cancellation.</strong>
        {' '}
        You may cancel your Subscription at any time. To cancel a Subscription, you must log
        into your account and use the cancellation mechanism provided prior to the next scheduled renewal date of your
        Subscription to avoid being charged for the next Subscription Term. If you cancel your Subscription, you may
        continue to use the applicable Paid Services until the end of your then-current Subscription Term, after which
        your ability to access the Paid Services will terminate. If you cancel your Subscription, you will not be
        eligible for a refund of any portion of the Subscription Fees paid for the then-current Subscription Term. If
        Raidbots does not receive payment for a Subscription renewal on or prior to the date of renewal, Raidbots may
        terminate or suspend your Subscription and/or access to the applicable Paid Services.
      </Item>
      <Item>
        <strong>Refunds.</strong>
        {' '}
        Except as otherwise set forth in the Terms of Use Agreement, below, or as required by applicable law,
        all Subscription purchases are final and Raidbots reserves the right, in its sole discretion, to provide or not
        provide a refund. For more information, please refer to our
        {' '}
        <LinkNewTab href="https://support.raidbots.com/article/19-refund-policy">
          Refund Policy
        </LinkNewTab>. If you are a consumer resident in an
        EU country, you can choose to cancel your Subscription purchase for any or no reason within the first 14 days
        from the day on which you receive confirmation of your Subscription purchase. To meet the cancellation deadline,
        you must cancel your Subscription using the method described in Section 7 of these Purchase and Payment Terms
        before the 14-day period has expired. If you successfully submit a cancellation request within the required
        14-day period, we will refund you the purchase price within 14 days from the date of receipt of your successful
        cancellation request. In making the refund, we will use the same means of payment as you used for your order,
        unless you and we agree otherwise.
      </Item>
    </ol>

  </>
);

const TermsOfUsePage = () => (
  <>
    <PageHelmet page="privacy" />
    <Flex justify="center">
      <Box lg={8} sm={12}>
        <TermsOfUse />
      </Box>
    </Flex>
  </>
);

export default TermsOfUsePage;
